
import { mapActions, mapState } from 'pinia';
import { ValidationObserver } from 'vee-validate';
import FormMixin from '@Mixins/form';
import { DELIVERY_FORM_MESSAGE_MAX_LENGTH } from '@Settings/common.settings';
import { FIELD_NAMES, FIELD_VALUES } from '@Settings/forms.settings';
import {
  DEFAULT_COUNTRY_CODE,
  MOBILE_PHONE_FIELDS_REGEX,
} from '@Settings/user.settings';
import { useUser } from '@Stores/user';
import errorHandler from '@Utils/error-handler';

export default {
  name: 'DeliveryForm',
  components: {
    ValidationObserver,
  },
  mixins: [FormMixin],
  props: {
    /**
     * Is displayed in Checkout page
     */
    isCheckout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const i18n = {
      ...this.$t('account.userForm'),
      legends: this.$t('forms.legends'),
      mandatory: this.$t('forms.mandatory'),
    };
    const labels = this.$t('forms.labels');
    const input = {
      size: 'l',
    };

    return {
      fields: {
        accessCode: {
          ...input,
          id: FIELD_NAMES.accessCode,
          label: labels.accessCode,
          name: FIELD_NAMES.accessCode,
        },
        addressLine2: {
          ...input,
          id: FIELD_NAMES.addressLine2,
          label: labels.addressLine2,
          name: FIELD_NAMES.addressLine2,
        },
        addressType: {
          id: FIELD_NAMES.addressType,
          inputs: [
            {
              input: {
                defaultValue: FIELD_VALUES.addressType.house,
                id: FIELD_NAMES.addressTypeHouse,
                name: FIELD_NAMES.addressType,
                value: FIELD_VALUES.addressType.house,
              },
              label: labels.house,
              type: 'radio',
            },
            {
              input: {
                defaultValue: FIELD_VALUES.addressType.apartment,
                id: FIELD_NAMES.addressTypeApartment,
                name: FIELD_NAMES.addressType,
                value: FIELD_VALUES.addressType.apartment,
              },
              label: labels.apartment,
              type: 'radio',
            },
          ],
          legend: labels.addressType,
          rules: {
            required: true,
          },
          validationName: 'addressType',
        },
        deliveryMessage: {
          ...input,
          id: FIELD_NAMES.deliveryMessage,
          label: labels.deliveryMessage,
          maxlength: DELIVERY_FORM_MESSAGE_MAX_LENGTH,
          name: FIELD_NAMES.deliveryMessage,
          type: 'textarea',
        },
        floor: {
          ...input,
          id: FIELD_NAMES.floor,
          label: labels.floor,
          name: FIELD_NAMES.floor,
          rules: {
            required: true,
          },
          validationName: 'floor',
        },
        intercom: {
          ...input,
          id: FIELD_NAMES.intercom,
          label: labels.intercom,
          name: FIELD_NAMES.intercom,
        },
        lift: {
          id: FIELD_NAMES.lift,
          inputs: [
            {
              input: {
                defaultValue: FIELD_VALUES.lift.withLift,
                id: FIELD_NAMES.withLift,
                name: FIELD_NAMES.lift,
                value: FIELD_VALUES.lift.withLift,
              },
              label: labels.with,
              type: 'radio',
            },
            {
              input: {
                defaultValue: FIELD_VALUES.lift.noLift,
                id: FIELD_NAMES.noLift,
                name: FIELD_NAMES.lift,
                value: FIELD_VALUES.lift.noLift,
              },
              label: labels.without,
              type: 'radio',
            },
          ],
          legend: labels.lift,
          rules: {
            required: true,
          },
          validationName: 'lift',
        },
        mobileCountryCode: {
          ...input,
          autocomplete: 'tel-country-code',
          errorMessage: false,
          id: FIELD_NAMES.countryCode,
          label: labels.countryCode,
          name: FIELD_NAMES.countryCode,
          options: [...this.$t('countries.countryCodes')],
        },
        mobilePhone: {
          ...input,
          autocomplete: 'tel-national',
          errorMessage: false,
          id: FIELD_NAMES.mobilePhone,
          inputmode: 'numeric',
          label: labels.mobilePhone,
          name: FIELD_NAMES.mobilePhone,
          rules: {
            // rules are ordered by priority
            required: true,
            nationalPhone: `@${FIELD_NAMES.countryCode}`,
            mobilePhone: `@${FIELD_NAMES.countryCode}`,
          },
          validationName: 'mobilePhone',
        },
      },

      i18n,

      submit: {
        attributes: {
          type: 'submit',
        },
        form: 'delivery-form',
        label: i18n.submit,
        size: 'l',
        variant: 3,
      },

      values: {
        accessCode: undefined,
        addressLine2: undefined,
        addressType: undefined,
        deliveryMessage: undefined,
        floor: undefined,
        intercom: undefined,
        lift: undefined,
        mobileCountryCode: DEFAULT_COUNTRY_CODE,
        mobilePhone: undefined,
      },
    };
  },
  computed: {
    ...mapState(useUser, {
      additionalInformation: (state) =>
        state.details?.deliveryInformation?.additionalInformations,
    }),

    floorRules() {
      return this.isApartment ? this.fields.floor.rules : undefined;
    },
    isApartment() {
      return this.values.addressType === FIELD_VALUES.addressType.apartment;
    },
    liftRules() {
      return this.isApartment ? this.fields.lift.rules : undefined;
    },
  },
  watch: {
    additionalInformation: {
      handler(info) {
        if (!info) {
          this.values = {
            accessCode: undefined,
            addressLine2: undefined,
            addressType: undefined,
            deliveryMessage: undefined,
            floor: undefined,
            intercom: undefined,
            lift: undefined,
            mobileCountryCode: DEFAULT_COUNTRY_CODE,
            mobilePhone: undefined,
          };

          return;
        }

        let lift, mobileCountryCode, mobilePhone;

        if (info.contactPhone) {
          const phoneValues = info.contactPhone?.match(
            MOBILE_PHONE_FIELDS_REGEX
          );
          if (phoneValues[1]) {
            mobileCountryCode = phoneValues[1];
          }

          mobilePhone = phoneValues[2];
        }

        if (typeof info.apartmentComplements?.hasLift !== 'undefined') {
          lift = info.apartmentComplements?.hasLift
            ? FIELD_VALUES.lift.withLift
            : FIELD_VALUES.lift.noLift;
        }

        this.values = {
          accessCode: info.apartmentComplements?.entryCode,
          addressLine2: info.apartmentComplements?.addressLine3,
          addressType: info.residenceType,
          deliveryMessage: info.specificities,
          floor: info.apartmentComplements?.floor,
          intercom: info.apartmentComplements?.intercom,
          lift,
          mobileCountryCode: mobileCountryCode || DEFAULT_COUNTRY_CODE,
          mobilePhone,
        };
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(useUser, ['storeDeliveryAdditionalInformation']),

    hasMobileError(errors) {
      return !!errors[this.fields.mobilePhone.id]?.length;
    },
    async onSubmit() {
      this.$emit('fetching', true);

      try {
        let apartmentComplements;
        let contactPhone = this.values.mobileCountryCode;

        if (this.values.mobilePhone[0] === '0') {
          contactPhone += this.values.mobilePhone.substring(1);
        } else {
          contactPhone += this.values.mobilePhone;
        }

        const isApartment =
          this.values.addressType === FIELD_VALUES.addressType.apartment;

        if (isApartment) {
          apartmentComplements = {
            addressLine3: this.values.addressLine2,
            entryCode: this.values.accessCode,
            floor: this.values.floor,
            hasLift: this.values.lift === FIELD_VALUES.lift.withLift,
            intercom: this.values.intercom,
          };
        }

        await this.storeDeliveryAdditionalInformation({
          apartmentComplements,
          contactPhone,
          residenceType: this.values.addressType,
          specificities: this.values.deliveryMessage,
        });

        this.$emit('update');
      } catch (err) {
        errorHandler(err, this.$nuxt, {
          path: 'patterns.delivery-form.onSubmit',
        });
      }

      this.$emit('fetching', false);
    },
  },
};
